
import { Component, Ref, Vue, Prop, Mixins } from "vue-property-decorator";
import { namespace, State } from "vuex-class";
import api from "@/services/api.service";
import { PickupCall } from "@/store/eventqueue/types";
import { User } from "@/store/types";
import { ResourceType, UpdateSessionParams } from "@/store/sessions/types";
import { AudioDevice, AudioOptions, AudioTypes, DeviceSpec, AudioX } from "@/types/audio/audio";

import IntercomAudioReceive from "@/components/event-queue/IntercomAudioReceive.vue";
import IntercomAudioTransmit from "@/components/event-queue/IntercomAudioTransmit.vue";

const Eventqueue = namespace("eventqueue");
const SessionStore = namespace("sessions");
const Devices = namespace("devices");

@Component({
	components: {
		"intercom-audio-receive": IntercomAudioReceive,
		"intercom-audio-transmit": IntercomAudioTransmit,
    }})
export default class IntercomModal extends Vue {	

	@SessionStore.Getter private getSession: (sessionId: number | string) => string;
	@SessionStore.Action private updateSession: (params: UpdateSessionParams) => Promise<void>;
	@State(state => state.User) user: User;
	@Eventqueue.State private showIntercomModal: boolean;
	@Eventqueue.Mutation private setShowIntercomModal: (visible: boolean) => void;
    @Devices.Action deviceQuickControl: ({ deviceId, reason }) => Promise<any>;

	@Ref() private cameraControl: any;

    @Prop(Object) currentCall: PickupCall;

	private selectedCallTemplate : PickupCall = {
		userId: 0,
		callId: 0,
		eventId: 0,
        areaName: null,
        deviceId: 0,
        deviceEndPoint: null,
		title: null
	};

	private selectedCall: PickupCall = { ...this.selectedCallTemplate };

    private loading:boolean = false;
    private speaking: boolean = false;
	private listening: boolean = false;
    
	private audioDevice: AudioDevice = null;
	private auth: string = "";

	private async mounted(): Promise<void> {
		await this.updateSession({ resourceId: ResourceType.Proxy });
		this.auth = this.getSession(ResourceType.Proxy.toString());		

        this.selectedCall = { ...this.currentCall };

        await this.cameraControl.startCamera(this.selectedCall.deviceId);

        await this.fetchAudio();		
	}

	private async beforeDestroyed() {
		if (this.selectedCall.callId == 0 || this.selectedCall.userId == 0) return;

		await this.cancelCall();
	}

    private async fetchAudio(): Promise<void> {        
        // NOTE a good improvement for this will be keeping the audio devices in the store so,
        // if the operator moves around the map a lot back and fore. We shouldn't need to fetch
        // all the same devices or some of the same devices etc. Future improvement!

        // reset audio device
        this.audioDevice = null;
        if (!this.selectedCall?.deviceId) return;

        const { data }: { data: AudioDevice[] } = await api.fetchAudioDevicesByIds(Array.of(this.selectedCall.deviceId));

        if (!data) return;
       
        this.audioDevice = data.find(first => first.deviceID);
    }
	
	private get getTitle(): string {
        return this.selectedCall.areaName && this.selectedCall.title 
		? `${this.selectedCall.areaName}: ${this.selectedCall.title}` 
		: "Incoming call...";
	}

	private async cancelCall() {
		this.setShowIntercomModal(false);
		//Call API to clear
		const res = await api.cancelCall(this.selectedCall);
		if (res?.status == 200) {
			this.selectedCall = { ...this.selectedCallTemplate };
		}
		else {
			console.error(`An exception occurred attempting to cancel the call for CallId: ${ this.selectedCall.callId }`);
		}

		if (this.cameraControl)
			await this.cameraControl.stop();
	}

	private async closeCall() {	
		this.setShowIntercomModal(false);		
		// Call API to clear
		const res = await api.closeCall(this.selectedCall);
		if (res?.status == 200) {
			this.selectedCall = { ...this.selectedCallTemplate };
		}
		else {
			console.error(`An exception occurred attempting to close the call for CallId: ${ this.selectedCall.callId }`);
		}

		if (this.cameraControl)
			await this.cameraControl.stop();
	}

	private get getRxAddress(): string {
        return this.constructAddress(AudioX.receive);
    }

	private get getTxAddress(): string {
        return this.constructAddress(AudioX.transmit);
    }	

	private constructAddress(audioType: AudioX): string {
        // What socket type we gonna use ws / wss
        const secure = location.protocol === "https:";
        let address = `${secure ? 'wss' : 'ws'}://${this.getDeviceServiceEndpoint}/devices/${audioType}?`;

        // Append auth.
        address += `auth=${this.auth}&`;

        // Append deviceX properties.
        let properties: DeviceSpec = null;
        
        // Get audio device type.  
		if (audioType === AudioX.receive) {
            properties = this.audioDevice.deviceSpec;
        } else if (audioType === AudioX.transmit) {
            properties = this.audioDevice.deviceSpec;
        }
		else {
            return "";
        }

        // construct our API call.
        Object.keys(properties).forEach((key: string, index: number) => {
            let data = properties[key];
            address += `${key}=${data ?? ''}${index !== (Object.keys(properties).length -1) ? '&' : ''}`;
        });

        return address;
    }

	/**
     * @summary method which will return the DeviceServiceEndpoint
     * this method also stricts out the extra bits which could exist like http/s etc.
     */
    private get getDeviceServiceEndpoint(): string {
        return this.selectedCall.deviceEndPoint.replace(/(^\w+:|^)\/\//, '');
    }
}
